const STORAGE_PREFIX = "smart360:idempotency";

const stableValue = (value) => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((result, key) => {
        result[key] = stableValue(value[key]);
        return result;
      }, {});
  }
  return value;
};

const contextFingerprint = (value) => {
  const content = JSON.stringify(stableValue(value));
  let first = 2166136261;
  let second = 2246822519;
  for (let index = 0; index < content.length; index += 1) {
    const code = content.charCodeAt(index);
    first = Math.imul(first ^ code, 16777619);
    second = Math.imul(second ^ code, 3266489917);
  }
  return [first, second]
    .map((part) => (part >>> 0).toString(16).padStart(8, "0"))
    .join("");
};

const hashValue = async (value) => {
  const content = JSON.stringify(stableValue(value));
  const bytes = new TextEncoder().encode(content);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

const decodeClaims = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    const payload = user?.token?.split(".")?.[1];
    if (!payload) return {};
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = JSON.parse(atob(normalized));
    const claims = decoded.claims || decoded;
    return {
      email: claims.email || "",
      company_id: claims.company_id || "",
      branch_office_id: claims.branch_office_id || "",
    };
  } catch (_) {
    return {};
  }
};

export const getStockDraftKey = () => {
  return `stockV1:v2:${contextFingerprint(decodeClaims())}`;
};

const storageKey = async (scope) => {
  const contextHash = await hashValue(decodeClaims());
  return `${STORAGE_PREFIX}:${contextHash}:${scope}`;
};

export const getIdempotencyKey = async ({ scope, method, endpoint, payload }) => {
  const key = await storageKey(scope);
  const payload_hash = await hashValue(payload);
  let stored = null;
  try {
    stored = JSON.parse(sessionStorage.getItem(key) || "null");
  } catch (_) {
    sessionStorage.removeItem(key);
  }

  const legacyIntent = stored?.idempotency_key ? stored : null;
  const intents = stored?.intents || {};
  if (legacyIntent) {
    const legacyFingerprint = `${legacyIntent.method}:${legacyIntent.endpoint}:${legacyIntent.payload_hash}`;
    intents[legacyFingerprint] = legacyIntent;
  }
  const fingerprint = `${method}:${endpoint}:${payload_hash}`;
  if (intents[fingerprint]) return intents[fingerprint].idempotency_key;

  const idempotency_key = crypto.randomUUID();
  intents[fingerprint] = {
    idempotency_key,
    payload_hash,
    method,
    endpoint,
    status: "PENDING",
  };
  sessionStorage.setItem(
    key,
    JSON.stringify({
      version: 2,
      intents,
    }),
  );
  return idempotency_key;
};

export const clearIdempotencyKey = async (scope, operation) => {
  const key = await storageKey(scope);
  if (!operation) {
    sessionStorage.removeItem(key);
    return;
  }
  let stored = null;
  try {
    stored = JSON.parse(sessionStorage.getItem(key) || "null");
  } catch (_) {
    sessionStorage.removeItem(key);
    return;
  }
  if (!stored?.intents) {
    sessionStorage.removeItem(key);
    return;
  }
  const payload_hash = await hashValue(operation.payload);
  const fingerprint = `${operation.method}:${operation.endpoint}:${payload_hash}`;
  delete stored.intents[fingerprint];
  if (Object.keys(stored.intents).length === 0) {
    sessionStorage.removeItem(key);
    return;
  }
  sessionStorage.setItem(key, JSON.stringify(stored));
};

export const isDefinitiveFailure = (error) =>
  [400, 403, 404, 422].includes(error?.status);
